import { Component } from '@angular/core';
import { forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HelpersService } from '../../core/services/helpers.service';
import { OperationsService } from '../../core/services/operations.service';
import { AttoAdapter } from '../components/fascicolo/atti-update/atto-adapter';
import { Atto } from '../models/atto';
import { DocumentoAllegato } from '../models/documento-allegato';
import { Provenienza } from '../models/tipologiche/provenienza';
import { TipoAtto } from '../models/tipologiche/tipo-atto';
import { TipoDocumento } from '../models/tipologiche/tipo-documento';
import { TipoTipologica } from '../models/tipologiche/tipo-tipologica';
import { VisibilitaDocumento } from '../models/tipologiche/visibilita-documento';
import { MetadatoDocumento } from './documento/document-uploaded/campi';
import { BaseForm } from './base-Form';
import { FascicoloFull } from '../models/fascicolo/fascicolo-full';

declare var removeEmptyObject: any;
@Component({
  selector: "div [atti-form]",
  templateUrl: "atti-form.component.html",
})
export abstract class AttiFormComponent extends BaseForm {
  public request: Atto;
  attiForm: FormGroup;
  isEdit = false;
  tipiAttoOptions: TipoAtto[] = [];
  private allTipiAttoOptions: TipoAtto[] = [];
  tipiProvenienzaOptions: Provenienza[] = [];
  alert = false;
  abstract getAtto(): Atto;
  abstract getRequest(request: Atto);
  abstract getFascicolo(): FascicoloFull;
  public isGestioneDocumenti: boolean;

  fileUploadList: DocumentoAllegato[] = [];
  docFormRequest: DocumentoAllegato;
  aggiungiDocumentoForm: FormGroup;
  tipiDocumento: TipoDocumento[] = [];
  tipiProvenienzaDoc: Provenienza[] = [];
  tipiVisibilitaDocumento: VisibilitaDocumento[] = [];
  docLoading = false;

  constructor(
    protected operationService: OperationsService,
    protected campiForm: FormBuilder,
    private helpers: HelpersService,
  ) {
    super();
  }

  /**
   * Punto di ingresso unico. Chiamare sempre questo metodo dall'esterno.
   * Inizializza request e poi:
   * - se le options non sono ancora caricate, chiama populateSelect che
   *   caricherà tutto e costruirà il form dentro la subscribe
   * - se le options sono già disponibili, setta il default e costruisce subito
   */
  initRequest(isEdit = false) {
    this.isEdit = isEdit;
    this.request = new AttoAdapter(this.getAtto()).atto;

    if (this.tipiAttoOptions.length === 0 || this.tipiProvenienzaOptions.length === 0) {
      this.populateSelect();
    } else {
      this.tipiAttoOptions = this.filterTipiAtto(this.allTipiAttoOptions);
      if (!this.isEdit && !this.request.provenienza) {
        const interno = this.tipiProvenienzaOptions.find(x => x.label === 'INTERNO');
        if (interno) this.request.provenienza = { ...interno };
      }
      this.tipiProvenienzaOptions = [...this.tipiProvenienzaOptions];
      this.buildForm();
    }
  }

  /**
   * Chiamato solo da initRequest o populateSelect, mai direttamente dall'esterno.
   * A questo punto request e tipiProvenienzaOptions sono già pronti.
   */
  private buildForm() {
    if (!this.isEdit) this.alert = false;

    if (!this.isEdit) {
      this.request.dataNotifica = Date.now();
    }

    this.attiForm = this.campiForm.group({
      tipoAtto: [
        this.request.tipoAtto ? this.request.tipoAtto : null,
        Validators.required,
      ],
      provenienza: [
        this.request.provenienza ? this.request.provenienza : null,
        Validators.required,
      ],
      numeroProtocollo: [
        this.request.numeroProtocollo ? this.request.numeroProtocollo : "",
      ],
      dataProtocollo: [
        this.request.dataProtocollo ? this.request.dataProtocollo : "",
      ],
      dataDeposito: [
        this.request.dataDeposito ? this.request.dataDeposito : "",
      ],
      dataNotifica: [
        this.request.dataNotifica ? this.request.dataNotifica : "",
        Validators.required,
      ],
      dataApposizioneFormulaEsecutiva: [
        this.request.dataApposizioneFormulaEsecutiva
          ? this.request.dataApposizioneFormulaEsecutiva
          : "",
      ],
      dataPubblicazione: [
        this.request.dataPubblicazione ? this.request.dataPubblicazione : "",
      ],
      titoloEsecutivo: [
        this.request.titoloEsecutivo ? this.request.titoloEsecutivo : false,
      ],
      note: [this.request.note ? this.request.note : ""],
    });

    this.checkTipoAttoSelection();
  }

  /**
   * Mantenuto per compatibilità con i componenti che lo chiamano dall'esterno.
   * Delega a initRequest.
   */
  createForm(isEdit = false) {
    this.initRequest(isEdit);
  }

  populateSelect() {
    forkJoin({
      tipiAtto: this.helpers.getTipiAtto(),
      tipiProvenienza: this.helpers.getTipiProvenienza()
    }).subscribe(({ tipiAtto, tipiProvenienza }) => {
      this.allTipiAttoOptions = [...tipiAtto];
      this.tipiAttoOptions = this.filterTipiAtto(this.allTipiAttoOptions);
      this.tipiProvenienzaOptions = tipiProvenienza;

      // Setta il default PRIMA di buildForm, così il comboMod
      // riceve objectReference e optionsCombo già allineati nello stesso ciclo
      if (!this.isEdit && !this.request.provenienza) {
        const interno = tipiProvenienza.find(x => x.label === 'INTERNO');
        if (interno) {
          this.request.provenienza = { ...interno };
        }
      }

      // Il form viene costruito con tutto già pronto
      this.buildForm();
    });
  }

  private filterTipiAtto(options: TipoAtto[]): TipoAtto[] {
    let result = [...options];
    const fascicolo = this.getFascicolo();
    if (fascicolo?.autoritaGiudiziaria) {
      if (fascicolo.autoritaGiudiziaria.label === 'CASSAZIONE') {
        const regex = /\(CASSAZIONE\)/;
        result = result.filter(x => x.isPct === true && regex.test(x.label));
      } else if (fascicolo.autoritaGiudiziaria.label === 'GIUDICE DI PACE') {
        const regex = /\(sigp\)/i;
        result = result.filter(x => x.isPct === true && regex.test(x.label));
      }
    }
    return result;
  }

  private checkTipoAttoSelection() {
    if (!this.isEdit || !this.tipiAttoOptions?.length) return;
    const valueType = this.request.tipoAtto?.label;
    const indexVal = this.tipiAttoOptions.findIndex(x => x.label === valueType);
    if (indexVal < 0) {
      this.request.tipoAtto = null;
      this.attiForm.controls['tipoAtto'].setValue(null);
      this.alert = true;
    } else {
      this.alert = false;
    }
  }

  compareById = (a: any, b: any): boolean => a?.id === b?.id;

  update() {
    this.getRequest(this.request);
  }

  initDocForm() {
    this.docFormRequest = new DocumentoAllegato();
    this.docFormRequest.dataRiferimento = new Date().getTime();
    this.fileUploadList = [];
    this.aggiungiDocumentoForm = this.campiForm.group({
      tipoVisibilitaDocumento: [null, Validators.required],
      note: [null],
    });
    this.helpers.getTipiDocumenti().subscribe(res => {
      this.tipiDocumento = res?.length ? res.sort((a: TipoDocumento, b: TipoDocumento) => a.label < b.label ? -1 : 1) : [];
    });
    this.helpers.getTipiProvenienzaDocumento().subscribe(res => {
      this.tipiProvenienzaDoc = res as Provenienza[];
      this.setDefaultProvenienzaDoc();
    });
    this.helpers.getTipiVisibilitaDocumento().subscribe(res => {
      this.tipiVisibilitaDocumento = res;
      this.setDefaultVisibilitaDoc();
    });
  }

  private setDefaultProvenienzaDoc() {
    if (!this.docFormRequest?.provenienza && this.tipiProvenienzaDoc?.length) {
      const interno = this.tipiProvenienzaDoc.find(p => p.label?.toUpperCase() === 'INTERNO');
      if (interno) {
        this.docFormRequest.provenienza = interno;
        this.aggiungiDocumentoForm?.get('provenienza')?.setValue(interno);
      }
    }
  }

  private setDefaultVisibilitaDoc() {
    if (!this.docFormRequest?.tipoVisibilitaDocumento && this.tipiVisibilitaDocumento?.length) {
      const pubblico = this.tipiVisibilitaDocumento.find(v => v.label?.toUpperCase() === 'PUBBLICO');
      if (pubblico) {
        this.docFormRequest.tipoVisibilitaDocumento = pubblico;
        this.aggiungiDocumentoForm?.get('tipoVisibilitaDocumento')?.setValue(pubblico);
      }
    }
  }

  setFileDataRequest(fileData: any) {
    const fileDaAllegare = new DocumentoAllegato();
    fileDaAllegare.refDocumentale = fileData.refDocumentale;
    fileDaAllegare.nomeFile = fileData.nomeFile;
    fileDaAllegare.contentType = fileData.contentType;
    fileDaAllegare.contentSize = fileData.contentSize;
    fileDaAllegare.signedData = fileData.signedData ?? null;
    fileDaAllegare.algoritmoImprontaDocumento = fileData.algoritmoImprontaDocumento;
    fileDaAllegare.improntaDocumento = fileData.improntaDocumento;
    fileDaAllegare.metadatoDocumento = {
      modalitaFormazioneDocumento: { id: 'A', descrizione: '', label: '', rimosso: false },
      tipoUfficioProduttore: { id: 'ESTERNO', descrizione: '', label: '', rimosso: false }
    } as MetadatoDocumento;
    this.fileUploadList = [...this.fileUploadList, fileDaAllegare];
  }

  onDocReturn(event: any) {
    if (!event?.refDocumentale) return;
    const idx = this.fileUploadList.findIndex(f => f.refDocumentale === event.refDocumentale);
    if (idx === -1) return;
    this.fileUploadList[idx].metadatoDocumento = {
      modalitaFormazioneDocumento: this.mapToTipoTipologica(event.modalitaFormazioneDocumento ?? 'A'),
      motivoAssenzaFirma: this.mapToTipoTipologica(event.motivoAssenzaFirma),
      documentoUnico: event.documentoUnico,
      tipoUfficioProduttore: this.mapToTipoTipologica(event.tipoUfficioProduttore ?? 'ESTERNO'),
      ufficioProduttore: event.ufficioProduttore,
      numDocumentiScansione: event.numDocumentiScansione,
      documentiScansionati: event.documentiScansionati
    } as MetadatoDocumento;
    this.fileUploadList = [...this.fileUploadList];
  }

  deleteDocFile(index: number) {
    this.fileUploadList.splice(index, 1);
    this.fileUploadList = [...this.fileUploadList];
  }

  onExtractArchive(event: { response: any; refDocumentale: string }) {
    const documenti: any[] = event.response.filesData;
    documenti.forEach(fd => this.setFileDataRequest(fd));
    const idx = this.fileUploadList.findIndex(f => f.refDocumentale === event.refDocumentale);
    if (idx >= 0) this.fileUploadList.splice(idx, 1);
    this.fileUploadList = [...this.fileUploadList];
  }

  mapToTipoTipologica(key?: string): TipoTipologica | undefined {
    if (!key) return undefined;
    return { id: key, descrizione: '', label: '', rimosso: false };
  }

  resetDocForm() {
    this.fileUploadList = [];
    this.docFormRequest = new DocumentoAllegato();
    this.docFormRequest.dataRiferimento = new Date().getTime();
    this.aggiungiDocumentoForm?.reset();
    this.setDefaultProvenienzaDoc();
    this.setDefaultVisibilitaDoc();
  }

  get canDocSave(): boolean {
    if (!this.fileUploadList?.length) return true;
    if (!this.docFormRequest?.tipoVisibilitaDocumento) return false;
    return this.fileUploadList.every(f => !!f.metadatoDocumento?.tipoUfficioProduttore);
  }

  onSearchChange(_files: any) {
    // no-op: overridden by AttiAddConcreteFormComponent
  }

  onTipoAttoChanged(tipoAtto: TipoAtto) {
    if (!this.aggiungiDocumentoForm) return;
    const found = tipoAtto?.label
      ? this.tipiDocumento.find(d => d.label === tipoAtto.label)
      : null;
    const tipoDoc = found ?? this.tipiDocumento.find(d => d.label?.toUpperCase() === 'VARIE') ?? null;
    this.docFormRequest.tipoDocumento = tipoDoc;
    this.aggiungiDocumentoForm.get('tipoDocumento')?.setValue(tipoDoc);
  }
}